import { useCallback, useEffect, useState } from 'react';
import { Address } from '@ton/core';
import i18n from '../i18n';
import { setLastIssuedKey } from '../ton/referral';
import {
  CONTRACT_PARAMS,
  IS_CONTRACT_CONFIGURED,
  LEGACY_LAST_WIN,
  TICKET_PRICE_TON,
  USE_PROXY,
} from '../ton/config';
import {
  readJettonMaster,
  readPrmdBalance,
  readPyramidaState,
  readUserByAddress,
  toTon,
} from '../ton/contract';

/**
 * UI-facing snapshot of the on-chain Pyramida state. Values are plain numbers
 * in TON / PRMD units for easy rendering; the shape mirrors the contract's
 * `PyramidaState` getter (contract/code_contracts/structs.tact).
 */
export interface PyramidaUiState {
  lottery: {
    prizePool: number; // real backable pool (TON): min(virtual bank, availableForPrize)
    availableForPrize: number; // real spendable ceiling (TON): balance − reserve − min
    projectedPrize: number; // real winner payout (TON): min(bank * winnerShare%, available)
    ownerShare: number; // real owner payout (TON): min(bank * ownerShare%, leftover)
    roundStakeTon: number; // currentRound.totalTickets (nanoTON-weighted stake) -> TON
    ticketPrice: number; // MIN_LOTTERY_ENTRY (TON)
    participantsCount: number; // currentRound.participantsCount
    roundEndTime: number; // currentRound.endTime (unix seconds)
    isDrawing: boolean; // a draw is currently being processed
    lastWinnerAddress: string | null;
    lastWinnerKey: number;
    lastWinAmount: number; // TON
    lastLotteryTime: number; // unix seconds (0 if never)
    totalWinners: number;
    totalWinsAmount: number; // TON
  };
  token: {
    totalMinted: number; // PRMD
    maxSupply: number; // PRMD
    lastMintTime: number; // unix seconds
    mintable: boolean;
  };
  system: {
    owner: string | null;
    jettonMasterAddress: string | null;
    totalUsers: number; // users.userCount (live, non-pruned)
  };
  /** Per-connected-wallet data; null until a wallet is connected & read. */
  me: {
    /** True once the on-chain identity read SUCCEEDED — only then is
     *  `registered: false` a fact rather than a default. */
    identityKnown: boolean;
    registered: boolean;
    key: number | null; // the user's own referrer key (for sharing)
    referralsCount: number;
    referralEarnings: number; // TON
    prmdBalance: number; // PRMD
    roundStakeTon: number; // this wallet's ticket weight in the current round (TON)
    roundStakeKnown: boolean; // false on the direct-RPC path (no per-round history)
  } | null;
  isLive: boolean; // true once wired to a real on-chain read
  lastUpdated: number | null; // ms epoch of the last successful read
  error: string | null;
}

/** Parses a user address, returning null instead of throwing on bad input. */
function tryParseAddress(raw: string): Address | null {
  try {
    return Address.parse(raw);
  } catch {
    return null;
  }
}

// Placeholder data used when no contract address is configured yet. Shaped
// exactly like a live read so swapping in real data is seamless.
function placeholderState(): PyramidaUiState {
  const weekFromNow = Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 7;
  return {
    lottery: {
      prizePool: 0,
      availableForPrize: 0,
      projectedPrize: 0,
      ownerShare: 0,
      roundStakeTon: 0,
      ticketPrice: TICKET_PRICE_TON,
      participantsCount: 0,
      roundEndTime: weekFromNow,
      isDrawing: false,
      lastWinnerAddress: null,
      lastWinnerKey: 0,
      lastWinAmount: 0,
      lastLotteryTime: 0,
      totalWinners: 0,
      totalWinsAmount: 0,
    },
    token: {
      totalMinted: 0,
      maxSupply: CONTRACT_PARAMS.maxSupply,
      lastMintTime: 0,
      mintable: true,
    },
    system: { owner: null, jettonMasterAddress: null, totalUsers: 0 },
    me: null,
    isLive: false,
    lastUpdated: null,
    error: null,
  };
}

/**
 * Reads the Pyramida contract state from chain. Falls back to placeholder data
 * when no contract address is configured. When `userAddress` is supplied and
 * the user is registered, per-wallet referral stats + PRMD balance are filled.
 */
export function usePyramidaState(userAddress?: string | null, pollMs = 5_000) {
  const [state, setState] = useState<PyramidaUiState>(placeholderState);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!IS_CONTRACT_CONFIGURED) {
      setState(placeholderState());
      return;
    }
    setLoading(true);
    try {
      // Kick off the per-wallet PRMD balance in parallel with the global state
      // read when it doesn't depend on it (the proxy path takes only the owner
      // address; the direct-RPC path needs the master, so it waits below).
      const addr = userAddress ? tryParseAddress(userAddress) : null;
      const balancePromise =
        addr && USE_PROXY ? readPrmdBalance(addr).catch(() => 0n) : null;

      const raw = await readPyramidaState();
      setLastIssuedKey(Number(raw.users.lastKey));
      const bank = toTon(raw.bankBalance);
      // The draw reconciles the virtual bank to the physically available funds
      // (bank.balance = availableForPrize) before taking the shares, so the
      // projected prize must use that same base or it overstates the payout.
      const available = toTon(raw.availableForPrize);
      const prizeBase = Math.min(bank, available);
      const winnerPrize = (prizeBase * CONTRACT_PARAMS.lotteryWinnerShare) / 100;
      const ownerPayout = (prizeBase * CONTRACT_PARAMS.lotteryOwnerShare) / 100;

      // Jetton master data (best-effort; master may be undeployed pre-init).
      let totalMinted = 0;
      let mintable = true;
      if (raw.jettonMasterAddress) {
        try {
          const jm = await readJettonMaster(raw.jettonMasterAddress);
          totalMinted = toTon(jm.totalSupply);
          mintable = jm.mintable;
        } catch {
          /* master not yet deployed — keep defaults */
        }
      }

      // Per-wallet data: an O(1) targeted getter (getUserByAddress) plus the
      // wallet's PRMD balance. Scales independently of total user count.
      let me: PyramidaUiState['me'] = null;
      if (addr) {
        me = {
          identityKnown: false,
          registered: false,
          key: null,
          referralsCount: 0,
          referralEarnings: 0,
          prmdBalance: 0,
          roundStakeTon: 0,
          roundStakeKnown: false,
        };
        try {
          const user = await readUserByAddress(addr);
          me.identityKnown = true;
          if (user.registered) {
            me.registered = true;
            // Key 0 on a regular wallet means "no personal invite key issued
            // yet" (it is assigned on the first buy). The ecosystem owner's
            // wallet legitimately shares key 0.
            me.key =
              user.key > 0n || addr.equals(raw.owner) ? Number(user.key) : null;
            me.referralsCount = Number(user.referralsCount);
            me.referralEarnings = toTon(user.earningsFromReferrals);
          }
          if (user.roundStakeNano !== null) {
            me.roundStakeTon = toTon(user.roundStakeNano);
            me.roundStakeKnown = true;
          }
          // Use the parallel proxy read if started; otherwise read now via RPC
          // (which needs the master derived from the global state above).
          const balance = balancePromise
            ? await balancePromise
            : await readPrmdBalance(addr, raw.jettonMasterAddress);
          me.prmdBalance = toTon(balance);
        } catch {
          /* read failed — leave defaults */
        }
      }

      setState({
        lottery: {
          prizePool: Math.min(bank, available),
          availableForPrize: available,
          projectedPrize: winnerPrize,
          ownerShare: ownerPayout,
          roundStakeTon: Number(raw.round.totalTickets) / 1e9,
          ticketPrice: TICKET_PRICE_TON,
          participantsCount: Number(raw.round.participantsCount),
          roundEndTime: Number(raw.round.endTime),
          isDrawing: raw.draw.isDrawing,
          // Until this contract produces its own winner, surface the previous
          // contract's real last payout instead of a blank/zero card.
          ...(raw.past.lastWinnerAddress
            ? {
                lastWinnerAddress: raw.past.lastWinnerAddress.toString(),
                lastWinnerKey: Number(raw.past.lastWinnerKey),
                lastWinAmount: toTon(raw.past.lastWinAmount),
                lastLotteryTime: Number(raw.past.lastLotteryTime),
              }
            : {
                lastWinnerAddress: LEGACY_LAST_WIN.winnerAddress,
                lastWinnerKey: LEGACY_LAST_WIN.winnerKey,
                lastWinAmount: toTon(LEGACY_LAST_WIN.amountNano),
                lastLotteryTime: LEGACY_LAST_WIN.time,
              }),
          totalWinners: Number(raw.past.totalWinners),
          totalWinsAmount: toTon(raw.past.totalWinsAmount),
        },
        token: {
          totalMinted,
          maxSupply: CONTRACT_PARAMS.maxSupply,
          lastMintTime: Number(raw.jetton.lastMintTime),
          mintable,
        },
        system: {
          owner: raw.owner.toString(),
          jettonMasterAddress: raw.jettonMasterAddress?.toString() ?? null,
          totalUsers: Number(raw.users.userCount),
        },
        me,
        isLive: true,
        lastUpdated: Date.now(),
        error: null,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : i18n.t('errors.readState');
      setState((s) => ({ ...s, error: message }));
    } finally {
      setLoading(false);
    }
  }, [userAddress]);

  useEffect(() => {
    refresh();
    if (pollMs <= 0) return;
    const id = setInterval(refresh, pollMs);
    return () => clearInterval(id);
  }, [refresh, pollMs]);

  return { state, loading, refresh };
}
